import { useCallback, useEffect, useRef, useState } from "react";
import classes from "./PreviousNext.module.css";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { COLOR_SECOND } from "../../commons/LaColors";
import { useLocation, useNavigate, useNavigationType } from "react-router";

type PreviousNextHistoryState = {
  previousPages: string[];
  currentPage: string;
};

type LocationStateWithPagination = {
  __lapediaPagination?: {
    [key: string]: PreviousNextHistoryState;
  };
  [key: string]: any;
};

const PreviousNext = (props: {
  nextPage: string | undefined;
  orderedBy?: string;
  getData: (orderedBy: string | undefined, page?: string) => void;
  historyKey?: string;
}) => {
  const FIRST_PAGE = "-1";
  const {
    nextPage,
    orderedBy,
    getData,
    historyKey = "default",
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const getLocationState = useCallback((): LocationStateWithPagination => {
    return (location.state as LocationStateWithPagination | null) || {};
  }, [location.state]);

  const getHistoryPaginationState = useCallback((): PreviousNextHistoryState | undefined => {
    return getLocationState().__lapediaPagination?.[historyKey];
  }, [getLocationState, historyKey]);

  const toRequestPage = (page: string): string | undefined => {
    return page === FIRST_PAGE ? undefined : page;
  };

  const initialHistoryState = getHistoryPaginationState();

  const [previousPages, setPreviousPages] = useState<string[]>(
    initialHistoryState?.previousPages || [],
  );
  const [currentPage, setCurrentPage] = useState<string>(
    initialHistoryState?.currentPage || FIRST_PAGE,
  );
  const currentPageRef = useRef(currentPage);
  const previousPagesRef = useRef(previousPages);
  const previousLocationKeyRef = useRef<string | null>(location.key);
  const hasProcessedInitialLocationRef = useRef(false);

  const scrollToTopAfterPop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, 0);
  };

  const persistPaginationState = (state: PreviousNextHistoryState) => {
    const locationState = getLocationState();

    const nextLocationState: LocationStateWithPagination = {
      ...locationState,
      __lapediaPagination: {
        ...(locationState.__lapediaPagination || {}),
        [historyKey]: state,
      },
    };

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      state: nextLocationState,
      replace: false,
    });
  };

  const syncState = (state: PreviousNextHistoryState) => {
    setCurrentPage(state.currentPage);
    setPreviousPages(state.previousPages);
  };

  const syncAndPersistState = (state: PreviousNextHistoryState) => {
    syncState(state);
    persistPaginationState(state);
  };

  useEffect(() => {
    currentPageRef.current = currentPage;
    previousPagesRef.current = previousPages;
  }, [currentPage, previousPages]);

  useEffect(() => {
    const historyState = getHistoryPaginationState();

    if (!hasProcessedInitialLocationRef.current) {
      hasProcessedInitialLocationRef.current = true;
    } else if (previousLocationKeyRef.current === location.key) {
      return;
    }

    previousLocationKeyRef.current = location.key;

    const shouldSyncState =
      historyState !== undefined &&
      (historyState.currentPage !== currentPageRef.current ||
        historyState.previousPages.join("|") !== previousPagesRef.current.join("|"));

    if (shouldSyncState) {
      syncState(historyState);
    }

    if (navigationType !== "POP") {
      return;
    }

    if (!historyState) {
      syncState({ previousPages: [], currentPage: FIRST_PAGE });
      getData(orderedBy, undefined);
      scrollToTopAfterPop();
      return;
    }

    getData(orderedBy, toRequestPage(historyState.currentPage));
    scrollToTopAfterPop();
  }, [getData, getHistoryPaginationState, location.key, navigationType, orderedBy]);

  const nextPreviousPageHandler = (next: boolean): string | undefined => {
    if (next) {
      if (nextPage !== undefined) {
        const nextState: PreviousNextHistoryState = {
          previousPages: [...previousPages, currentPage],
          currentPage: nextPage,
        };
        syncAndPersistState(nextState);
      }
      return nextPage;
    }

    if (previousPages.length === 0) {
      console.error("no previous page"); // TODO-adi: throw
      return undefined;
    }

    const previousPage = previousPages[previousPages.length - 1] || FIRST_PAGE;
    const previousState: PreviousNextHistoryState = {
      previousPages: previousPages.slice(0, -1),
      currentPage: previousPage,
    };
    syncAndPersistState(previousState);

    return toRequestPage(previousPage);
  };

  return (
    <div className={`${classes.previousNext} `}>
      {previousPages.length !== 0 && (
        <div className={classes.previous}>
          <GoChevronLeft
            color={COLOR_SECOND}
            className="cursor-pointer"
            size={44}
            onClick={() => {
              let page = nextPreviousPageHandler(false);
              getData(orderedBy, page);
            }}
          />
        </div>
      )}
      {nextPage !== undefined ? (
        <div className={classes.next}>
          <GoChevronRight
            color={COLOR_SECOND}
            size={44}
            className="cursor-pointer"
            onClick={() => {
              let page = nextPreviousPageHandler(true);
              getData(orderedBy, page);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
export default PreviousNext;
